/**
 * Generic tooltip renderer for composable report charts.
 * Responsible for layout and rendering of configured tooltip rows.
 */
import { Box, Typography } from '@mui/material';

import { buildGenericTooltipRows } from '../../model/lib/tooltipHelpers';

import type {
    ReportWidgetItem,
    ReportWidgetTooltipRowConfig,
    ReportWidgetTooltipRowsBuilder,
} from '../../model/types';
import type { TooltipProps } from 'recharts';

type RechartsTooltipPayload = TooltipProps<number, string>['payload'];

export interface ReportWidgetTooltipProps {
    active?: boolean;
    payload?: RechartsTooltipPayload;
    label?: string;
    total: number;
    items: ReportWidgetItem[];
    tooltipFormatter: (value: number) => string;
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
}

export function ReportWidgetTooltip({
    active,
    payload,
    label,
    total: chartTotal,
    items: chartItems,
    tooltipFormatter,
    tooltipRowsBuilder,
}: Readonly<ReportWidgetTooltipProps>) {
    if (!active || !payload?.length) {
        return null;
    }

    let rows: ReportWidgetTooltipRowConfig[] | undefined;

    if (tooltipRowsBuilder) {
        rows = tooltipRowsBuilder({
            payload,
            label,
            valueFormatter: tooltipFormatter,
            items: chartItems,
            total: chartTotal,
        });
    } else {
        rows = buildGenericTooltipRows({
            payload,
            label,
            valueFormatter: tooltipFormatter,
            items: chartItems,
            total: chartTotal,
        });
    }

    if (!rows || rows.length === 0) {
        return null;
    }

    return (
        <Box
            data-testclass="report-widget-chart-tooltip"
            sx={{
                backgroundColor: 'background.paper',
                borderRadius: '8px',
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
                border: '1px solid',
                borderColor: 'divider',
                padding: '20px',
                width: '260px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            }}
        >
            {rows.map((row) => {
                if (row.variant === 'divider') {
                    return (
                        <Box
                            key={row.id}
                            sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                marginTop: row.marginTop ?? '2px',
                                marginBottom: row.marginBottom ?? '2px',
                            }}
                        />
                    );
                }

                const isHeader = row.variant === 'header';

                let labelWeight = row.boldLabel ? 600 : 400;
                if (isHeader) {
                    labelWeight = 700;
                }

                return (
                    <Box
                        key={row.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: row.marginTop
                                ? `${row.marginTop}px`
                                : undefined,
                            marginBottom: row.marginBottom
                                ? `${row.marginBottom}px`
                                : undefined,
                        }}
                    >
                        {row.showSwatch && (
                            <Box
                                sx={{
                                    flexGrow: 0,
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: row.swatchColor,
                                    marginRight: '8px',
                                }}
                            />
                        )}
                        {row.label && (
                            <Typography
                                component="div"
                                variant="body2"
                                sx={{
                                    fontWeight: labelWeight,
                                    fontSize: '1.3rem',
                                    lineHeight: '1.6rem',
                                    display: '-webkit-box',
                                    flex: 1,
                                    minWidth: 0,
                                    marginRight: '12px',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: row.mutedLabel
                                        ? 'rgba(0, 0, 0, 0.50)'
                                        : row.labelColor ?? 'common.black',
                                }}
                            >
                                {row.label}
                            </Typography>
                        )}
                        {row.value && (
                            <Typography
                                component="div"
                                variant="body2"
                                sx={{
                                    fontWeight: row.boldValue ? 600 : 400,
                                    fontSize: '1.3rem',
                                    lineHeight: '1.6rem',
                                    marginLeft: 'auto',
                                    textAlign: 'right',
                                    flexShrink: 0,
                                    color: row.positiveValue
                                        ? 'metrics.positive'
                                        : row.valueColor ?? 'common.black',
                                }}
                            >
                                {row.value}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
